import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type { Tariff, TariffComponent } from "../types";
import { useToast } from "../toast";
import TariffDialog from "../dialogs/TariffDialog";
import ConfirmDialog from "../dialogs/ConfirmDialog";

function componentUnit(component: TariffComponent): string {
  if (component.type === "energy") return "/kWh";
  return "/min";
}

function componentSummary(components: TariffComponent[]): string {
  if (!components.length) return "—";
  return components
    .map((component) => {
      const startAfter =
        component.start_after_minutes > 0 ? ` after ${component.start_after_minutes}m` : "";
      const hasRanges = component.time_ranges?.length ? " (time-limited)" : "";
      return `${component.type}: ${component.price.toFixed(3)} ${component.currency}${componentUnit(
        component
      )}${startAfter}${hasRanges}`;
    })
    .join(" · ");
}

export default function Tariffs() {
  const api = useAuthApi();
  const [items, setItems] = useState<Tariff[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<Tariff | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<Tariff | null>(null);
  const [search, setSearch] = useState("");
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api<Tariff[]>(`${MGMT}/tariffs`);
      setItems(data ?? []);
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api(`${MGMT}/tariffs/${deleting.id}`, { method: "DELETE" });
      toast.show("Tariff deleted");
      setDeleting(null);
      load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((t) => {
      const haystack = [
        t.name,
        t.currency,
        t.description,
        ...(t.components ?? []).map((c) => c.type),
        t.is_active ? "active" : "inactive",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [items, search]);

  const hasFilters = search.trim().length > 0;

  return (
    <>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreating(true)}
        >
          Add Tariff
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small"
          placeholder="Filter tariffs…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {hasFilters && <Button size="small" onClick={() => setSearch("")}>Clear filters</Button>}
      </Stack>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell>Components</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      {hasFilters ? "No tariffs match the current filters." : "No tariffs yet."}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((t) => (
              <TableRow key={t.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{t.name}</TableCell>
                <TableCell>{t.currency}</TableCell>
                <TableCell>{componentSummary(t.components ?? [])}</TableCell>
                <TableCell>
                  <Chip
                    label={t.is_active ? "Active" : "Inactive"}
                    color={t.is_active ? "success" : "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(t)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(t)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TariffDialog
        open={creating || editing !== null}
        tariff={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={() => {
          setCreating(false);
          setEditing(null);
          load();
        }}
      />
      <ConfirmDialog
        open={deleting !== null}
        title="Delete tariff?"
        message={`Are you sure you want to delete "${deleting?.name}"?`}
        onCancel={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </>
  );
}
